import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Typography,
  Skeleton,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import request from "../../../utilities/requests";
import { toast } from "../../../utilities/SnackBarUtils";

const BANNER_HEIGHT = "50vh";

const ProfileHeader = ({ profile, refresh }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const inputFile = useRef(null);
  const bannerRef = useRef(null);
  const dragStartRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [firstName, setFirstName] = useState(profile?.firstName || "");
  const [lastName, setLastName] = useState(profile?.lastName || "");
  const [previewUrl, setPreviewUrl] = useState(null);
  const lastImageUrl = useRef(null);

  const [focalX, setFocalX] = useState(profile?.photoFocalX ?? 50);
  const [focalY, setFocalY] = useState(profile?.photoFocalY ?? 30);
  const [draftFocalX, setDraftFocalX] = useState(focalX);
  const [draftFocalY, setDraftFocalY] = useState(focalY);
  const [repositionMode, setRepositionMode] = useState(false);
  const [savingFocal, setSavingFocal] = useState(false);

  useEffect(() => {
    if (profile) {
      setFirstName(profile?.firstName);
      setLastName(profile?.lastName);

      if (previewUrl && profile?.imageUrl && profile.imageUrl !== lastImageUrl.current) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      lastImageUrl.current = profile?.imageUrl;

      const fx = profile?.photoFocalX ?? 50;
      const fy = profile?.photoFocalY ?? 30;
      setFocalX(fx);
      setFocalY(fy);
    }
  }, [profile]);

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    const localPreview = URL.createObjectURL(file);
    setPreviewUrl(localPreview);
    // Reset focal point to default after new upload
    setFocalX(50);
    setFocalY(30);

    const body = new FormData();
    body.append("photo", file);
    body.append("Content-Type", "image/jpg");
    const token = localStorage.getItem(
      `${process.env.REACT_APP_COGNITO_CLIENT_ID}.accessToken`
    );
    setLoading(true);
    axios({
      url: `${process.env.REACT_APP_TNFT_SERVER}/profile/photo`,
      method: "post",
      data: body,
      headers: { "Content-Type": "multipart/form-data", Accesstoken: token },
    })
      .then(() => {
        // Also persist the focal reset so the server matches local state
        request("profile/update", "post", { photoFocalX: 50, photoFocalY: 30 });
        toast.success("Profile image updated");
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        toast.error("Error uploading profile image");
        refresh();
      });
  };

  const handleSaveName = async () => {
    const result = await request("profile/update", "post", { firstName, lastName });
    if (result.success) {
      refresh();
      setOpen(false);
    }
  };

  const handleEnterReposition = () => {
    setDraftFocalX(focalX);
    setDraftFocalY(focalY);
    setRepositionMode(true);
  };

  const handleCancelReposition = () => {
    setRepositionMode(false);
  };

  const handleSaveFocal = async () => {
    setSavingFocal(true);
    try {
      const result = await request("profile/update", "post", {
        photoFocalX: draftFocalX,
        photoFocalY: draftFocalY,
      });
      if (!result.success) throw new Error(result.error);
      setFocalX(draftFocalX);
      setFocalY(draftFocalY);
      setRepositionMode(false);
      toast.success("Photo position saved");
    } catch (e) {
      toast.error("Error saving photo position");
    } finally {
      setSavingFocal(false);
    }
  };

  const handlePointerDown = (e) => {
    if (e.target.closest("button")) return;
    e.preventDefault();
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      focalX: draftFocalX,
      focalY: draftFocalY,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!dragStartRef.current) return;
    const container = bannerRef.current;
    if (!container) return;
    const { width, height } = container.getBoundingClientRect();
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    // Dragging right reveals the left side of the image → focalX decreases
    const newX = Math.round(Math.min(100, Math.max(0, dragStartRef.current.focalX - (dx / width) * 100)));
    const newY = Math.round(Math.min(100, Math.max(0, dragStartRef.current.focalY - (dy / height) * 100)));
    setDraftFocalX(newX);
    setDraftFocalY(newY);
  };

  const handlePointerUp = () => {
    dragStartRef.current = null;
  };

  const photoUrl = previewUrl || profile?.imageUrl;
  const hasName = Boolean(profile?.firstName && profile?.lastName);
  const displayFocalX = repositionMode ? draftFocalX : focalX;
  const displayFocalY = repositionMode ? draftFocalY : focalY;

  return (
    <div style={{ width: "100%" }}>
      {/* Banner wrapper - shrink-wraps the banner so the camera badge anchors to
          the photo's actual corner (the banner itself clips its overflow). */}
      <div style={{ position: "relative" }}>
      {/* Photo banner - full width, flush to top */}
      <div
        ref={bannerRef}
        style={{
          position: "relative",
          width: "100%",
          height: BANNER_HEIGHT,
          overflow: "hidden",
          borderBottomLeftRadius: "10rem",
          borderBottomRightRadius: "10rem",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.3)",
          background: "#eee",
          cursor: repositionMode ? "grab" : "default",
          touchAction: repositionMode ? "none" : "auto",
        }}
        onPointerDown={repositionMode ? handlePointerDown : undefined}
        onPointerMove={repositionMode ? handlePointerMove : undefined}
        onPointerUp={repositionMode ? handlePointerUp : undefined}
      >
        {loading ? (
          <Skeleton variant="rectangular" width="100%" height={BANNER_HEIGHT} />
        ) : photoUrl ? (
          <img
            src={photoUrl}
            alt="Profile"
            draggable={false}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: `${displayFocalX}% ${displayFocalY}%`,
              display: "block",
              userSelect: "none",
              pointerEvents: "none",
            }}
          />
        ) : (
          <AccountCircleIcon
            style={{
              color: "grey",
              width: "100%",
              height: "100%",
              position: "absolute",
              top: 0,
              left: 0,
            }}
          />
        )}

        {/* Drag hint in reposition mode */}
        {repositionMode && (
          <div
            style={{
              position: "absolute",
              top: 12,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <Typography
              variant="caption"
              style={{
                backgroundColor: "rgba(0,0,0,0.55)",
                color: "white",
                padding: "4px 10px",
                borderRadius: 4,
              }}
            >
              Drag to reposition
            </Typography>
          </div>
        )}

        {/* Reposition button - centered at the bottom, clear of the corner
            camera badge. */}
        {!repositionMode && photoUrl && (
          <Button
            size="small"
            variant="contained"
            style={{
              position: "absolute",
              bottom: 24,
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: "rgba(0,0,0,0.5)",
              color: "white",
              textTransform: "none",
              whiteSpace: "nowrap",
            }}
            onClick={handleEnterReposition}
          >
            Reposition
          </Button>
        )}

        {/* Save / Cancel in reposition mode */}
        {repositionMode && (
          <div
            style={{
              position: "absolute",
              bottom: 16,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
              gap: 8,
              pointerEvents: "none",
            }}
          >
            <Button
              size="small"
              variant="contained"
              color="primary"
              disabled={savingFocal}
              style={{ pointerEvents: "all" }}
              onClick={handleSaveFocal}
            >
              Save
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={savingFocal}
              style={{ backgroundColor: "rgba(255,255,255,0.85)", pointerEvents: "all" }}
              onClick={handleCancelReposition}
            >
              Cancel
            </Button>
          </div>
        )}

      </div>

      {/* Change-photo badge - white circle, coral camera. Pinned just inside the
          photo's rounded bottom-right corner. Hidden while repositioning. */}
      {!repositionMode && (
        <IconButton
          data-tour="profile-photo"
          aria-label={
            photoUrl
              ? t("profile.changephoto.button")
              : t("profile.addphoto.button")
          }
          onClick={() => inputFile.current.click()}
          style={{
            position: "absolute",
            bottom: 28,
            right: 28,
            zIndex: 2,
            width: 46,
            height: 46,
            backgroundColor: "#fff",
            boxShadow: "0 4px 14px rgba(0,0,0,0.28)",
          }}
        >
          <PhotoCameraIcon
            style={{ color: theme.palette.primary.main, width: 24, height: 24 }}
          />
        </IconButton>
      )}
      </div>

      {/* Below-banner content */}
      <Grid
        container
        direction="column"
        spacing={2}
        justifyContent="center"
        alignItems="center"
        style={{ marginTop: 16 }}
      >
        {/* Name - a tappable chip that opens the edit dialog. Empty state shows
            a coral-bordered "Add your name" prompt instead of a red error. */}
        <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
          <div
            data-tour="profile-name"
            onClick={() => setOpen(true)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              border: `1.5px solid ${hasName ? "#efe6e0" : theme.palette.primary.main}`,
              background: "#fbf6f2",
              borderRadius: 12,
              padding: "9px 16px",
              cursor: "pointer",
            }}
          >
            <Typography
              variant="h5"
              style={{
                color: hasName ? undefined : "#a1968f",
                fontWeight: hasName ? undefined : 500,
              }}
            >
              {hasName
                ? `${profile.firstName} ${profile.lastName}`
                : t("profile.addname.label")}
            </Typography>
            <EditIcon style={{ color: theme.palette.primary.main, width: 20 }} />
          </div>
        </Grid>
      </Grid>

      <input
        type="file"
        id="file"
        ref={inputFile}
        style={{ display: "none" }}
        onChange={handleUpload}
      />

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{t("profile.editnamedialog.title")}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label={t("profile.editfirstname.label")}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <TextField
            fullWidth
            margin="dense"
            label={t("profile.editlastname.label")}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">
            {t("dialog.cancel.button")}
          </Button>
          <Button onClick={handleSaveName} color="primary">
            {t("dialog.save.button")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ProfileHeader;
